import { useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { ArrowUpDown } from 'lucide-react';
import Lang from 'lang.js';
import dayjs from 'dayjs';
import {
  IconCircleFilled,
  IconCrown,
  IconSend,
  IconUserCancel,
  IconUserOff,
} from '@tabler/icons-react';
import { DropdownMenuItem } from '@radix-ui/react-dropdown-menu';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { getInitials } from '@/utils/stringHelpers';
import {
  useMutateReinviteToOrg,
  useMutateCancelOrgInvitation,
  useMutateTransferOrganizationalOwnership,
  useMutationsChangeRole,
} from '@/reactQuery/post';
import { OrganizationRoles } from '@/enums';
import TooltipWrapper from '../tooltip/TooltipWrapper';
import useOrganizationRoles from '@/hooks/useOrganizationRoles';
import { Member, RoleChange } from '@/types';
import { CancelInvitationToOrgModal, ReinviteToOrgModal } from '../Modals';

const UserColumns = (
  lang: Lang,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rolesData: string[],
  setOpenDeletingModal: React.Dispatch<React.SetStateAction<boolean>>,
  setRowUserName: React.Dispatch<React.SetStateAction<string>>,
  organizationId: string,
) => {
  const { mutateAsync } = useMutationsChangeRole(organizationId);
  const { mutateAsync: mutateAsyncReinvite } = useMutateReinviteToOrg(organizationId);
  const { mutateAsync: mutateAsyncCancelInvitation } = useMutateCancelOrgInvitation(organizationId);
  const { mutateAsync: mutateAsyncTransferOwnership } = useMutateTransferOrganizationalOwnership();
  const { isUserOwner: isUserOwnerOrg } = useOrganizationRoles();

  const [openReinviteModal, setOpenReinviteModal] = useState(false);
  const [openCancelInviteModal, setOpenCancelInviteModal] = useState(false);
  const [pendingReinviteData, setPendingReinviteData] = useState<{
    email: string;
    role: string;
  } | null>(null);
  const [pendingCancelData, setPendingCancelData] = useState<{
    userId: string;
    email: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleRoleChange = async (data: RoleChange) => {
    try {
      await mutateAsync(data);
      toast.success(lang.get('msg.userRoleSuccessfullyUpdated'));
    } catch (err) {
      toast.error(lang.get('msg.userRoleNotUpdated'));
    }
  };

  const handleOwnershipTransfer = async (formData: { user_id: string }) => {
    try {
      await mutateAsyncTransferOwnership({
        user_id: formData.user_id,
        organization_id: organizationId,
      });

      toast.success(lang.get('msg.ownershipSuccessfullyTransfered'));
    } catch (err) {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
    }
  };

  const handleReinvitationConfirm = async () => {
    if (!pendingReinviteData) return;

    setIsLoading(true);
    try {
      await mutateAsyncReinvite({
        email: pendingReinviteData.email,
        role: pendingReinviteData.role,
      });
      toast.success(lang.get('msg.userSuccessfullyReinvited'));
      setOpenReinviteModal(false);
      setPendingReinviteData(null);
    } catch (err) {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleReinvitation = (data: { email: string; role: string }) => {
    setPendingReinviteData(data);
    setOpenReinviteModal(true);
  };

  const handleCancelInvitationConfirm = async () => {
    if (!pendingCancelData) return;

    setIsLoading(true);
    try {
      await mutateAsyncCancelInvitation({ user_id: pendingCancelData.userId });
      toast.success(lang.get('msg.invitationSuccessfullyCancelled'));
      setOpenCancelInviteModal(false);
      setPendingCancelData(null);
    } catch {
      toast.error(lang.get('msg.errorPleaseTryAgain'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelInvitation = (data: { userId: string; email: string }) => {
    setPendingCancelData(data);
    setOpenCancelInviteModal(true);
  };

  const handleCloseReinviteModal = () => {
    setOpenReinviteModal(false);
    setPendingReinviteData(null);
  };

  const handleCloseCancelModal = () => {
    setOpenCancelInviteModal(false);
    setPendingCancelData(null);
  };

  const columns: ColumnDef<Member>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="pl-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          {lang.get('msg.name')}
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center  uppercase border rounded-full  min-w-[36px] min-h-[36px]">
            <Avatar>
              {/* add img later */}
              <AvatarImage src="" />
              <AvatarFallback className="font-semibold text-dark-text">
                {row?.getValue('name') ? (
                  getInitials(row?.getValue('name'))
                ) : (
                  <IconUserOff className="text-light-dark" size={15} />
                )}
              </AvatarFallback>
            </Avatar>
          </div>
          <p>
            {row?.getValue('name') ?? (
              <span className="capitalize whitespace-nowrap">
                {lang.get('msg.accountCreationPending')}
                ...
              </span>
            )}
          </p>
        </div>
      ),
    },
    {
      accessorKey: 'email',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="pl-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          Email
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="lowercase">
          <a
            className="transition-all ease-in-out hover:text-primary"
            href={`mailto:${row.getValue('email')}`}
          >
            {row.getValue('email')}
          </a>
        </div>
      ),
    },
    {
      accessorKey: 'invite_accepted',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="pl-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          {lang.get('msg.status')}
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <div
            className={`uppercase text-xs whitespace-nowrap rounded-full font-bold w-fit px-3.5 p-1 ${
              (row.getValue('invite_accepted') === true && 'bg-green-200/50 text-green-600') ||
              (row.getValue('invite_accepted') === false && 'bg-orange-200/50 text-orange-600') ||
              ''
            }`}
          >
            {row.getValue('invite_accepted') === true
              ? lang.get('msg.accepted')
              : lang.get('msg.pending')}
          </div>
          {row.getValue('invite_accepted') === false && (
            <>
              <TooltipWrapper
                text={
                  row?.original?.invitations[0]?.reinvite_request
                    ? lang.get('msg.resendInvitationRequestedByUser')
                    : lang.get('msg.resendInvitation')
                }
                className="mb-1"
              >
                <Button
                  className="relative px-2 py-2 h-fit"
                  variant="ghost"
                  onClick={() =>
                    handleReinvitation({ email: row?.original?.email, role: row?.original?.role })
                  }
                >
                  {row?.original?.invitations?.[0]?.reinvite_request && (
                    <IconCircleFilled className="absolute text-red-500 top-0.5 left-1" size={9} />
                  )}
                  <IconSend size={18} />
                </Button>
              </TooltipWrapper>
              <TooltipWrapper text={lang.get('msg.cancelInvitation')} className="mb-1">
                <Button
                  className="relative px-2 py-2 h-fit"
                  variant="ghost"
                  onClick={() =>
                    handleCancelInvitation({
                      userId: row?.original?._id,
                      email: row?.original?.email,
                    })
                  }
                >
                  <IconUserCancel size={18} />
                </Button>
              </TooltipWrapper>
            </>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'role',
      header: ({ column }) => (
        <Button
          variant="ghost"
          className="pl-0"
          onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
        >
          {lang.get('msg.userRole')}
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      ),
      cell: ({ row }) => {
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [selectedRole, setSelectedRole] = useState<string | undefined>(row.getValue('role'));
        return (
          <div>
            <DropdownMenu>
              <DropdownMenuTrigger
                asChild
                disabled={
                  row?.original?.invite_accepted === false ||
                  row.original.role === OrganizationRoles.OWNER ||
                  !isUserOwnerOrg
                }
              >
                <div className="relative flex">
                  <Button
                    disabled={
                      row?.original?.invite_accepted === false ||
                      row.original.role === OrganizationRoles.OWNER ||
                      !isUserOwnerOrg
                    }
                    variant="outline"
                    className="capitalize outline-none select-none ring-offset-transparent ring-transparent focus-visible:ring-0 focus-visible:shadow-none focus-visible:ring-opacity-0 disabled:bg-gray-100 disabled:cursor-not-allowed "
                  >
                    {row?.original?.role?.toLowerCase()}
                  </Button>
                </div>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-64">
                <DropdownMenuRadioGroup
                  value={selectedRole?.toString()}
                  onValueChange={(value: string) => {
                    if (value === OrganizationRoles.OWNER) {
                      return;
                    }
                    if (value === row?.id) {
                      setOpenDeletingModal(true);
                      setRowUserName(row?.getValue('name'));
                      return;
                    }
                    setSelectedRole(value);
                    if (row?.original?.role !== value) {
                      handleRoleChange({
                        role: value,
                        user_id: row?.original?._id,
                      });
                    }
                  }}
                >
                  {rolesData?.map((role: string) => (
                    <DropdownMenuRadioItem
                      key={role}
                      className={`flex items-center gap-2 px-5 py-3 cursor-pointer 
                                ${selectedRole === role && 'bg-neutral-100'}`}
                      value={role}
                    >
                      {lang.get(`msg.${role}`)}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
                {isUserOwnerOrg && (
                  <DropdownMenuItem
                    onClick={() => handleOwnershipTransfer({ user_id: row?.original?._id })}
                    className="flex items-center gap-2 px-5 py-3 border-none outline-none cursor-pointer hover:bg-neutral-100 text-[14px]"
                  >
                    <p>Transfer Ownership</p>
                    <IconCrown className="text-yellow-600" />
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
    {
      accessorKey: 'updatedAt',
      header: () => <div className="text-right">{lang.get('msg.dateCreated')}</div>,
      cell: ({ row }) => (
        <div className="text-right capitalize">
          {dayjs(row.getValue('updatedAt')).format('DD/MM/YYYY')}
        </div>
      ),
    },
  ];

  return {
    columns,
    modals: (
      <>
        <ReinviteToOrgModal
          isOpen={openReinviteModal}
          onClose={handleCloseReinviteModal}
          onConfirm={handleReinvitationConfirm}
          email={pendingReinviteData?.email || ''}
          isLoading={isLoading}
        />
        <CancelInvitationToOrgModal
          isOpen={openCancelInviteModal}
          onClose={handleCloseCancelModal}
          onConfirm={handleCancelInvitationConfirm}
          email={pendingCancelData?.email || ''}
          isLoading={isLoading}
        />
      </>
    ),
  };
};

export default UserColumns;
